/**
 * "Will Instagram take this?" — answered on the tile, before anyone composes.
 *
 * The networks disagree about the same file in ways nobody should have to hold
 * in their head: Instagram accepts JPEG only where Facebook accepts five
 * formats, caps a feed image at 8 MB where Facebook allows 10, and refuses
 * anything outside 4:5–1.91:1 where Facebook constrains nothing (migration
 * 20260821000400). A library that shows a thumbnail and a file size is hiding
 * the one fact that decides where the file can go.
 *
 * The verdicts come from the same `validate-media` function Compose uses — the
 * rules live in the database and are cited to the platform's own documentation,
 * so nothing here decides anything. This module only draws the answer.
 */
import {
  PLATFORM_LABEL, type Platform, type PostKind, type Problem, type ValidationResult,
} from "../lib/types";
import { PLATFORM_ICON } from "./PlatformIcons";

/** Fixed, so the same network sits in the same place on every tile. */
const ORDER: Platform[] = ["facebook", "instagram", "youtube"];

const rank = (p: Platform) => {
  const i = ORDER.indexOf(p);
  return i === -1 ? ORDER.length : i;
};

export const sortFit = (results: ValidationResult[]): ValidationResult[] =>
  [...results].sort((a, b) => rank(a.platform) - rank(b.platform));

/**
 * Three states, not two. A video that fits every stated limit still carries the
 * codec warning the validator cannot check before upload, and calling that a
 * clean pass would be the promise this product must not make.
 */
export type Fit = "ok" | "warn" | "no";

export function fitOf(result: ValidationResult): Fit {
  if (!result.ok) return "no";
  return result.problems.some((p) => p.severity === "warning") ? "warn" : "ok";
}

/**
 * One problem, said the way the library needs it said.
 *
 * `no_rule` is the registry answering "there is no such row" — the right thing
 * for Compose to refuse on, and a non-sequitur beside a photo in a library. The
 * true statement for that case is about the product, not the registry.
 */
function sentence(problem: Problem, who: string, kind: PostKind | undefined): string {
  if (problem.code !== "no_rule") return problem.message;
  return kind
    ? `We do not publish ${kind === "video" ? "videos" : "photos"} to ${who}.`
    : `We do not publish this kind of file to ${who}.`;
}

/** The whole verdict in a tooltip, because the tile has room for an icon and nothing else. */
export function fitTitle(result: ValidationResult, kind?: PostKind): string {
  const who = PLATFORM_LABEL[result.platform];
  const say = (p: Problem) => sentence(p, who, kind);
  const errors = result.problems.filter((p) => p.severity === "error");
  if (errors.length > 0) return `${who} — ${errors.map(say).join(" ")}`;
  const warnings = result.problems.filter((p) => p.severity === "warning");
  if (warnings.length > 0) return `${who} — fits, with a caveat: ${warnings.map(say).join(" ")}`;
  return `${who} — fits`;
}

/**
 * The tile row. `results` undefined means the answer is still coming, and the
 * row keeps its height while it does so a grid of a hundred tiles does not
 * reflow under the cursor.
 */
export function MediaFitRow({ results, kind }: { results?: ValidationResult[]; kind?: PostKind }) {
  // Empty means the check itself failed. A badge nobody asked for must not
  // become a broken badge, so the row simply is not there.
  if (results?.length === 0) return null;

  if (!results) {
    return (
      <span className="fit-row" aria-label="Checking which networks accept this file">
        {ORDER.map((platform) => {
          const Icon = PLATFORM_ICON[platform];
          return (
            <span key={platform} className="fit checking" title="Checking…">
              <Icon />
            </span>
          );
        })}
      </span>
    );
  }

  return (
    <span className="fit-row">
      {sortFit(results).map((result) => {
        const Icon = PLATFORM_ICON[result.platform];
        const title = fitTitle(result, kind);
        // Two states on the tile, three in the detail. Every video carries the
        // codec caveat, and painting all of them amber here would cry wolf on
        // the one screen that has to be readable at a glance.
        return (
          <span key={result.platform} className={`fit ${result.ok ? "ok" : "no"}`} title={title}>
            <Icon />
            <span className="visually-hidden">{title}</span>
          </span>
        );
      })}
    </span>
  );
}

const VERDICT: Record<Fit, { chip: string; word: string }> = {
  ok: { chip: "chip ok", word: "Fits" },
  warn: { chip: "chip warn", word: "Fits, with a caveat" },
  no: { chip: "chip err", word: "Not accepted" },
};

/**
 * The same verdicts with their reasons, for the detail view — where there is
 * room to say *why* a 12 MB PNG is fine on Facebook and refused by Instagram,
 * and to link the specification that says so.
 */
export function MediaFitDetail(
  { results, kind }: { results?: ValidationResult[]; kind?: PostKind },
) {
  if (!results) return <p className="muted">Checking which networks accept this file…</p>;
  if (results.length === 0) {
    return <p className="muted">Could not check the networks just now — reload to try again.</p>;
  }

  return (
    <>
      {sortFit(results).map((result) => {
        const Icon = PLATFORM_ICON[result.platform];
        const verdict = VERDICT[fitOf(result)];
        return (
          <div key={result.platform} className="target-verdict">
            <div className="row">
              <Icon />
              <strong>{PLATFORM_LABEL[result.platform]}</strong>
              {/* Instagram publishes a video as a reel, YouTube as a video: the
                  placement is what the limits below actually belong to. There is
                  none to name when the registry has no row at all. */}
              {result.sourceUrl && <span className="muted">{result.placement}</span>}
              <span className="spacer" />
              <span className={verdict.chip}>{verdict.word}</span>
            </div>

            {result.problems.map((p, i) => (
              <div key={i} className={`problem ${p.severity}`}>
                {sentence(p, PLATFORM_LABEL[result.platform], kind)}
              </div>
            ))}

            {result.sourceUrl && !result.ok && (
              <div className="muted fit-source">
                <a href={result.sourceUrl} target="_blank" rel="noreferrer">
                  {PLATFORM_LABEL[result.platform]}'s published specification
                </a>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
